import {
    type PermissionSourceDesignator,
    type PermissionsGrant,
    IpgSystemIdToName,
    IpwSystemIdToName,
    resourceDesignatorKey,
} from '@pillar-portal/api-types';
import { Badge } from '@/components/ui/badge';
import {
    AuthorizableAction,
    ResourceType,
    type ResourceType as ResourceTypeValue,
} from '@pillar-portal/database/dist/frontend';
import { cn } from '@/lib/utils';

const RESOURCE_TYPE_LABELS: Record<ResourceTypeValue, string> = {
    [ResourceType.ORGANIZATION]: 'Organization',
    [ResourceType.USER]: 'User',
    [ResourceType.CREDENTIAL]: 'Credential',
    [ResourceType.PERMISSIONS_BUNDLE]: 'Permissions Bundle',
    [ResourceType.PERMISSION_POLICY]: 'Permission Policy',
    [ResourceType.ROLE_ASSIGNMENT]: 'Role Assignment',
    [ResourceType.RPA_MODULE]: 'RPA Module',
    [ResourceType.IPW]: 'IPW',
    [ResourceType.IPG]: 'IPG',
    [ResourceType.PROCESSOR]: 'Processor',
    [ResourceType.MERCHANT_ACCOUNT]: 'Merchant Account',
    [ResourceType.LOGICAL_TERMINAL]: 'Logical Terminal',
    [ResourceType.TERMINAL_DEVICE]: 'Terminal Device',
};

const RESOURCE_TYPE_PLURAL_LABELS: Record<ResourceTypeValue, string> = {
    [ResourceType.ORGANIZATION]: 'organizations',
    [ResourceType.USER]: 'users',
    [ResourceType.CREDENTIAL]: 'credentials',
    [ResourceType.PERMISSIONS_BUNDLE]: 'permissions bundles',
    [ResourceType.PERMISSION_POLICY]: 'permission policies',
    [ResourceType.ROLE_ASSIGNMENT]: 'role assignments',
    [ResourceType.RPA_MODULE]: 'RPA modules',
    [ResourceType.IPW]: 'IPW systems',
    [ResourceType.IPG]: 'IPG systems',
    [ResourceType.PROCESSOR]: 'processors',
    [ResourceType.MERCHANT_ACCOUNT]: 'merchant accounts',
    [ResourceType.LOGICAL_TERMINAL]: 'logical terminals',
    [ResourceType.TERMINAL_DEVICE]: 'terminal devices',
};

const AUTHORIZABLE_ACTION_VERB_LABELS: Record<AuthorizableAction, string> = {
    [AuthorizableAction.USE]: 'use',
    [AuthorizableAction.LIST]: 'list',
    [AuthorizableAction.DISPLAY]: 'display',
    [AuthorizableAction.CREATE]: 'create',
    [AuthorizableAction.EDIT]: 'edit',
    [AuthorizableAction.DELETE]: 'delete',
    [AuthorizableAction.GRANT]: 'grant',
    [AuthorizableAction.REVOKE]: 'revoke',
    [AuthorizableAction.TAKE_PAYMENT]: 'take payment',
    [AuthorizableAction.VOID_PAYMENT]: 'void payment',
    [AuthorizableAction.REFUND_PAYMENT]: 'refund payment',
    [AuthorizableAction.OVERRIDE_FEE]: 'override fees',
    [AuthorizableAction.IMPERSONATE]: 'impersonate',
    [AuthorizableAction.AUDIT]: 'audit',
};

export interface PermissionGrantSummaryProps {
    permission: PermissionsGrant;
    compact?: boolean;
    organizationNameByUuid?: Readonly<Record<string, string>>;
    designatorLabelByKey?: Readonly<Record<string, string>>;
}

export function formatPermissionResourceTypeLabel(resourceType: ResourceTypeValue): string {
    return RESOURCE_TYPE_LABELS[resourceType];
}

export function formatPermissionResourceTypePluralLabel(
    resourceType: ResourceTypeValue
): string {
    return RESOURCE_TYPE_PLURAL_LABELS[resourceType];
}

export function formatPermissionActionVerbList(
    actions: readonly AuthorizableAction[]
): string {
    const actionVerbs: string[] = actions.map(
        (action) => AUTHORIZABLE_ACTION_VERB_LABELS[action]
    );
    if (actionVerbs.length === 0) {
        return '';
    }
    if (actionVerbs.length === 1) {
        return actionVerbs[0] ?? '';
    }
    if (actionVerbs.length === 2) {
        return `${actionVerbs[0] ?? ''} and ${actionVerbs[1] ?? ''}`;
    }
    const leadingActionVerbs = actionVerbs.slice(0, -1);
    const trailingActionVerb = actionVerbs.at(-1) ?? '';
    return `${leadingActionVerbs.join(', ')}, and ${trailingActionVerb}`;
}

export function formatPermissionDesignatorLabel(
    resource: PermissionSourceDesignator,
    targetResourceType?: ResourceTypeValue,
    organizationNameByUuid?: Readonly<Record<string, string>>,
    designatorLabelByKey?: Readonly<Record<string, string>>
): string {
    if ('designator_type' in resource) {
        if (targetResourceType === undefined) {
            return 'All in-scope target resources';
        }
        return `All in-scope ${formatPermissionResourceTypeLabel(targetResourceType).toLowerCase()} resources`;
    }
    const resolvedLabel: string | undefined =
        designatorLabelByKey?.[resourceDesignatorKey(resource)];
    if (resolvedLabel !== undefined) {
        return resolvedLabel;
    }
    switch (resource.resource_type) {
        case ResourceType.ORGANIZATION: {
            const organizationLabel =
                organizationNameByUuid?.[resource.organization_uuid] ??
                resource.organization_uuid;
            return resource.descendants
                ? `Organization ${organizationLabel} and descendants`
                : `Organization ${organizationLabel}`;
        }
        case ResourceType.USER:
            return `User ${resource.user_uuid}`;
        case ResourceType.ROLE_ASSIGNMENT:
            return `Role assignment ${resource.role_assignment_uuid}`;
        case ResourceType.CREDENTIAL:
            return `Credential ${resource.credential_uuid}`;
        case ResourceType.IPW:
            return `IPW ${IpwSystemIdToName[resource.ipw_system_id]}`;
        case ResourceType.IPG:
            return `IPG ${IpgSystemIdToName[resource.ipg_system_id]}`;
        case ResourceType.PROCESSOR:
            return `Processor ${resource.processor_uuid}`;
        case ResourceType.MERCHANT_ACCOUNT:
            return `Merchant account ${resource.merchant_account_uuid}`;
        case ResourceType.LOGICAL_TERMINAL:
            return `Logical terminal ${resource.logical_terminal_uuid}`;
        case ResourceType.TERMINAL_DEVICE:
            return `Terminal device ${resource.terminal_device_uuid}`;
        case ResourceType.PERMISSIONS_BUNDLE:
            return `Permissions bundle ${resource.permissions_bundle_uuid}`;
        case ResourceType.PERMISSION_POLICY:
            return `Permission policy ${resource.permission_policy_uuid}`;
        case ResourceType.RPA_MODULE:
            return `RPA module ${resource.rpa_module_name}`;
    }
}

export function PermissionGrantSummary({
    permission,
    compact = false,
    organizationNameByUuid,
    designatorLabelByKey,
}: PermissionGrantSummaryProps) {
    const containerClassName = compact ? 'space-y-1' : 'space-y-3';
    const resourceLabelClassName = compact
        ? 'text-xs bg-gray-100 px-1.5 py-0.5 rounded'
        : 'text-xs bg-gray-100 px-2 py-1 rounded';

    return (
        <div className={containerClassName}>
            <div className="text-sm">
                <span className="font-medium">Resource type:</span>{' '}
                <span>{formatPermissionResourceTypeLabel(permission.resource_type)}</span>
            </div>

            <div>
                <span className="text-sm font-medium">Actions:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                    {permission.actions.map((action, index) => (
                        <Badge
                            key={`${permission.resource_type}-${action}-${index}`}
                            variant="outline"
                        >
                            {action}
                        </Badge>
                    ))}
                </div>
            </div>

            <div>
                <span className="text-sm font-medium">Sources:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                    {permission.resources.map((resource, index) => (
                        <span
                            key={`${index}-${formatPermissionDesignatorLabel(resource, permission.resource_type, organizationNameByUuid, designatorLabelByKey)}`}
                            className={cn(resourceLabelClassName)}
                        >
                            {formatPermissionDesignatorLabel(
                                resource,
                                permission.resource_type,
                                organizationNameByUuid,
                                designatorLabelByKey
                            )}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
